import {
  PlaneGeometry,
  Mesh,
  MeshStandardMaterial,
  Scene,

  Texture,
  Vector3,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Group,
  MeshBasicMaterial,
  Color,
  MeshLambertMaterial,
} from "three";
import WoodPlanksOutline from "./woodplank_outlines";
import PoolBorders from "./pool_borders";
import AssetManager from "../utils/asset_manager";
import UVHelpers from "../utils/uv_helpers";
import { extendShaderWithUVGradient, extendShaderWithWorldGradient } from "../utils/shader_utils";

class GardenOutline {
  
  private gardenBorders: Mesh[] = [];
  private grassPatches: InstancedMesh[] = [];
  private woodPlanksOutline: WoodPlanksOutline;
  private poolBorders: PoolBorders;
  private gardenSize: number;
  private gardenMaterial: MeshStandardMaterial;
  private timeOfDay = 'day';
  private grassMat: MeshBasicMaterial
  private isInEditMode:boolean=false;

  constructor(
    woodPlanksOutline: WoodPlanksOutline,
    poolBorders: PoolBorders,
    gardenSize: number = 3.0
  ) {
    this.woodPlanksOutline = woodPlanksOutline;
    this.poolBorders = poolBorders;
    this.gardenSize = gardenSize;

    this.gardenMaterial = new MeshStandardMaterial({
      toneMapped: true,
      color: "#253317",
      map: AssetManager.getInstance().getAsset("grass_color") as Texture,
      roughness: 1,
      metalness: 0,
      metalnessMap: AssetManager.getInstance().getAsset("grass_rough") as Texture,
      aoMap: AssetManager.getInstance().getAsset("grass_ao") as Texture,
      wireframe: false,
    });
    

    this.gardenMaterial.needsUpdate=true;

    this.grassMat = new MeshBasicMaterial({
      color: "#699932",
      toneMapped: true,
      side: 2,
    });

   // this.grassMat.clippingPlanes = [new PlaneGeometry(0, 0, 1, 1)];

    this.grassMat.needsUpdate=true;
    this.createGardenOutline();
  }

  resize(scene: Scene) {
    this.removeFromScene(scene);
    this.createGardenOutline();
    this.addToScene(scene);
  }

  addToScene(scene: Scene) {
    this.gardenBorders.forEach((border) => scene.add(border));
    if (!this.isInEditMode) this.grassPatches.forEach((patch) => scene.add(patch));
  }

  removeFromScene(scene: Scene) {
    this.gardenBorders.forEach((border) => scene.remove(border));
    this.gardenBorders = [];

    this.removeGardenPatches(scene);
  }

  private removeGardenPatches(scene: Scene) {
    this.grassPatches.forEach(function (patch) {
      patch.dispose();
      return scene.remove(patch);
    });

    this.grassPatches = [];
  }

  private createGardenOutline() {
    const halfLength =
      this.poolBorders.poolLength / 2 + this.poolBorders.margin + this.woodPlanksOutline.outlineSize;
    const halfWidth =
      this.poolBorders.poolWidth / 2 + this.poolBorders.margin + this.woodPlanksOutline.outlineSize + this.gardenSize;

    this.createBorder(-halfLength, -halfWidth, halfLength, -halfWidth + this.gardenSize); // Bottom border
    this.createBorder(-halfLength, halfWidth - this.gardenSize, halfLength, halfWidth); // Top border
  }

  private createBorder(x1: number, z1: number, x2: number, z2: number) {
    const width = x2 - x1;
    const height = z2 - z1;
    const geometry = new PlaneGeometry(width, height);
    UVHelpers.autoUV({ geometry });
    const border = new Mesh(geometry, this.gardenMaterial);
    border.layers.set(2);
    border.position.set((x1 + x2) / 2, 0, (z1 + z2) / 2);
    border.rotation.x = -Math.PI / 2;
    border.receiveShadow = true;

    this.createGrassPatchesForBorder(border, 0.08);
    this.gardenBorders.push(border);
  }

  private createGrassPatchesForBorder(border: Mesh, spacing: number = 100.0) {
    const gridPositions = this.createGridAboveBorder(border, spacing);
    const grassMesh = (AssetManager.getInstance().getAsset("grass") as Group).children[0] as Mesh;

    grassMesh.castShadow = false;
    grassMesh.receiveShadow = false;
    grassMesh.layers.set(2);
   

    extendShaderWithUVGradient(this.grassMat, 0.1, 0.2)

    if (this.isInEditMode) return;

    const instanceCount = gridPositions.length;
   // console.log("Creating " + instanceCount + " grass patches");

   // console.log('grassMesh.geometry is buffergeometry : ' + grassMesh.geometry.isBufferGeometry);
    const instancedGrass = new InstancedMesh(grassMesh.geometry, this.grassMat, instanceCount);
    instancedGrass.matrixWorldNeedsUpdate = false;
    instancedGrass.matrixAutoUpdate = false;
    instancedGrass.frustumCulled=false;
    instancedGrass.castShadow=false;
    instancedGrass.receiveShadow=false;

    const matrix = new Matrix4();
    gridPositions.forEach((position, i) => {
      const scale = 0.0007 + Math.random() * 0.002;
      const randomPositionOffset = new Vector3(
        Math.random() * 0.03-Math.random() * 0.03,
        -0.02,
        Math.random() * 0.03-Math.random() * 0.03
      );
      matrix.compose(
        position.add(randomPositionOffset),
        grassMesh.quaternion.setFromAxisAngle(new Vector3(1, 0, 0), -Math.random() * Math.PI / 2.0),
        
        grassMesh.scale.set(scale*1.1, scale*1.1, scale*1.1)
      );
      
      instancedGrass.setMatrixAt(i, matrix);

    });

   // instancedGrass.rotation.x = -Math.PI / 2;

    instancedGrass.layers.set(2);
    this.grassPatches.push(instancedGrass);
  }

  /**
   * Creates a grid of Vector3 positions above a specified border.
   * @param {Mesh} border - The border above which to create the grid.
   * @param {number} spacing - The spacing between grid points.
   * @returns {Vector3[]} - The array of Vector3 positions.
   */
  createGridAboveBorder(border: Mesh, spacing: number = 1.0): Vector3[] {
    const gridPositions: Vector3[] = [];
    const borderGeometry = border.geometry as THREE.PlaneGeometry;  // Assumes border is a Mesh with PlaneGeometry
    const width = borderGeometry.parameters.width;
    const height = borderGeometry.parameters.height;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const startX = border.position.x - halfWidth;
    const startZ = border.position.z - halfHeight;
    const endX = border.position.x + halfWidth;
    const endZ = border.position.z + halfHeight;

    for (let x = startX; x <= endX; x += spacing) {
      for (let z = startZ; z <= endZ; z += spacing) {
        const position = new Vector3(x, 0, z);
        gridPositions.push(position);
      }
    }

    return gridPositions;
  }

  setTimeOfDay(type:string) {
    console.log('set grass time of day to ' + type);
    if (type=='day') {
      console.log('set the grass color to day');

      

      this.grassMat.color.setHex(0x699932);
      
    }
    else {
      console.log('set the grass color to night');
      this.grassMat.color.setHex(0x3A556B);
      
    }

    this.grassMat.needsUpdate=true;
  }

  setEditMode(isEditMode: boolean, scene:Scene) {
    this.isInEditMode=isEditMode;
    
    if (isEditMode) {
      this.gardenMaterial.wireframe=true;
      this.removeGardenPatches(scene);
    }
    else {
      this.gardenMaterial.wireframe=false;
      this.resize(scene);
    }
  }

}

export default GardenOutline;
